/**
 * Lista + detalhe do layout A + C.
 *
 * `<ListDetail list={<DataTable …/>} detail={sel && <DetailPanel …/>} />` — a lista ocupa sempre 100% da largura; o
 * detalhe abre como gaveta sobreposta à direita só quando há item escolhido (sem `detail`, nada é reservado).
 *
 * `<DetailPanel title subtitle meta actions onClose label size>{seções}</DetailPanel>` — gaveta à direita
 * (`role="dialog"`, `aria-modal`), por cima da página com o fundo escurecido:
 * - `subtitle`: linha pequena em fonte mono acima do título ("keycloak · StatefulSet · worker-03");
 * - `meta`: linha abaixo do título (usuário, local, porta do switch…);
 * - `children`: o corpo, normalmente `<DetailSection title>`s, `KeyValue`, gráficos;
 * - `actions`: botões no pé do corpo (o primeiro, a ação principal, com `btn btn-primary`);
 * - `onClose`: botão de fechar, Esc e clique no fundo; o foco fica preso na gaveta e volta à linha ao fechar;
 * - `size`: 'md' (padrão, até 460 px) ou 'lg' (até 720 px, para detalhes com tabelas).
 *
 * `<DetailSection title>` — bloco com título pequeno dentro do painel.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from './icons';
import { cx } from './layout';
import { useFocusTrap } from './overlay';

export function ListDetail({ list, detail, className }: { list: ReactNode; detail?: ReactNode; className?: string }) {
  return (
    <div className={cx('list-detail', !!detail && 'with-detail', className)}>
      <div className="ld-list">{list}</div>
      {detail}
    </div>
  );
}

export function DetailPanel({
  title,
  subtitle,
  meta,
  actions,
  onClose,
  label,
  children,
  className,
  size = 'md',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  /** Nome acessível (padrão: o título). */
  label?: string;
  children?: ReactNode;
  className?: string;
  size?: 'md' | 'lg';
}) {
  const { t } = useI18n<CommonKey>();
  const id = useId();
  const box = useRef<HTMLElement>(null);
  useFocusTrap(box, true);
  // Esc fecha a gaveta (menos quando um modal aberto por cima trata o Esc)
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.scrim-modal')) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="detail-scrim" onClick={onClose} aria-hidden="true" />
      <aside
        ref={box}
        className={cx('detail-panel', size === 'lg' && 'dp-lg', className)}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={label}
        aria-labelledby={label ? undefined : id}
      >
        <header className="dp-head">
          <div className="dp-titles">
            {subtitle && <div className="dp-sub">{subtitle}</div>}
            <h2 className="dp-title" id={id}>
              {title}
            </h2>
            {meta && <div className="dp-meta">{meta}</div>}
          </div>
          {onClose && (
            <button type="button" className="icon-btn dp-close" aria-label={t('ui.detail.close')} title={t('ui.detail.close')} onClick={onClose}>
              <Icon name="x" size="sm" />
            </button>
          )}
        </header>
        <div className="dp-body">
          {children}
          {actions && <div className="dp-actions">{actions}</div>}
        </div>
      </aside>
    </>
  );
}

export function DetailSection({ title, children, className }: { title?: ReactNode; children: ReactNode; className?: string }) {
  const id = useId();
  return (
    <section className={cx('dp-sec', className)} aria-labelledby={title ? id : undefined}>
      {title && (
        <h3 className="dp-sec-title" id={id}>
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}
